import { alpha, type Theme } from '@mui/material'
import { authPx } from './authScale'

/**
 * The liquid-glass card behind the auth form — sign-in AND sign-up, one
 * definition (2026-09-23). Spread into the form column's `sx`; the column
 * also carries `data-auth-column`, which is what the ambient lobes centre on.
 *
 * ── HOW IT IS BUILT ─────────────────────────────────────────────────────────
 * A `::before` with a NEGATIVE inset, so the column's measured layout — and
 * every spacing budget recorded on these pages (sign-up's "fits 1280x680 with
 * no vertical scroll") — is untouched: the card is drawn AROUND the column,
 * not added to it. It also follows the column's width for free, which sign-up
 * needs: its container widens between steps.
 *
 * `isolation: isolate` is load-bearing. The pseudo sits at `zIndex: -1`, which
 * must resolve inside the COLUMN'S stacking context — behind the form, in
 * front of the ambient lobes. Sign-in's column has one (position + zIndex 2);
 * sign-up's only has one while its entrance `transform` is running, after
 * which a -1 would fall behind the fixed backdrop and the card would vanish.
 *
 * ── THE DECISIONS, IN THE ORDER THEY WERE MADE (Spencer, 2026-09-23) ────────
 *   "what about putting the login centre in a liquid glass card?"
 *   "try the card frosted"  -> blur 28 -> 56px at the same fill. More OPACITY
 *     greyed the dark card and cut colour inside the light one 39%; more BLUR
 *     kept it (measured in the card margins, dark 26.8 -> 28.6).
 *   "darker background on dark mode and light on light — it shows too much
 *     colour through the glass" -> tinted with the PAGE (background.default),
 *     not white: dark 28.6 -> 12.4, light 14.9 -> 5.7. A faint white sheen
 *     over the top 45% keeps it reading as glass rather than a flat panel.
 *   "sharper card border radius — more modern" -> 28 -> 16px, 2x the 8px of
 *     every input and button (packages/ui theme).
 *
 * Cost, measured with the lobes animating behind it: real GPU 208 -> 198 fps
 * (p95 frame 8.4ms both); headless, no GPU, 4x CPU throttle 15.3 -> 9.3.
 * Blur radius is the lever if a low-end report ever arrives.
 *
 * Phones: the default card's 40px sides do not fit beside a full-width form at
 * 390px, so below `sm` EVERY auth page gets the solid phone sheet instead (see
 * `PHONES` below, 2026-10-08).
 */

/**
 * The page ground at these alphas, top-left -> bottom-right.
 *
 * THE MIDPOINT OF THE TWO CARDS THAT USED TO DISAGREE (2026-10-30). Sign-in ran
 * 0.58-0.74 and sign-up ran `solid` at 0.86-0.93, so the same chrome read as two
 * different materials depending on which door you came through — sign-in glassy
 * with the lobes showing, sign-up nearly opaque. Spencer: "these don't match in
 * font size or liquid glass blurryness ... maybe blur somewhere in between the
 * two."
 *
 * Worth being precise about what moved, because the word was "blur" and blur is
 * NOT what differed: both cards declare `blur(56px) saturate(150%)` and always
 * did. The visible difference was entirely this FILL ALPHA. Blur is unchanged.
 *
 * Arithmetic, per channel: dark (0.58+0.88)/2 = 0.73, (0.70+0.93)/2 = 0.815;
 * light (0.62+0.86)/2 = 0.74, (0.74+0.92)/2 = 0.83.
 *
 * Both desktop cards now read this — sign-up dropped its `density: 'solid'` — so
 * they cannot drift apart again by one being retuned alone. `solid` survives for
 * the PHONE sheet, which every auth page still gets unconditionally (2026-10-08,
 * and unaffected by this).
 */
const CARD_TINT = { dark: [0.73, 0.815], light: [0.74, 0.83] } as const

/**
 * `density: 'solid'` — sign-up's panel (Spencer, 2026-10-08: "less of the blobs
 * showing ... a more opaque liquid glass panel so the background reads as
 * ambient, not busy"). Same construction as the default card, three changes:
 *
 *   1. FILL ~0.88-0.93 of the page ground instead of 0.58-0.74, so the form sits
 *      on a near-solid surface and the lobes read only at the card's edges and
 *      around it. Blur stays (it is what keeps the edges looking like glass).
 *   2. A slightly stronger top highlight so the denser panel still reads as
 *      glass rather than a flat sheet.
 *
 * Sign-in keeps the default ON DESKTOP — it was tuned on its own (see the
 * decision log above). Switching it is one argument.
 *
 * ── PHONES (below `sm`): EVERY AUTH PAGE, ALWAYS SOLID ─────────────────────
 * Spencer, 2026-10-08, after sign-up got its phone panel: "all the platform auth
 * pages ... should keep a liquid-glass panel behind the form, like desktop, so
 * the purple background blobs don't show through so much." On a phone the
 * sign-in form sat directly on the lobes. So the phone sheet no longer depends
 * on `density`: every caller gets it, at the solid fill, as a full-width sheet
 * (the column's phone gutter is exactly its side inset, so it lands flush with
 * the screen) with a 20px radius and no side borders. `desktop: false` is for
 * pages that never had a desktop card (reset password, verify email, the
 * post-payment screen): they get the phone sheet and nothing changes on desktop.
 */
export type AuthGlassDensity = 'default' | 'solid'

const SOLID_TINT = { dark: [0.88, 0.93], light: [0.86, 0.92] } as const

type CardOpts = {
  /** Desktop (`sm`-up) fill. Phones are always solid. */
  density?: AuthGlassDensity
  /** `false`: no desktop card (the page never had one); the phone sheet only. */
  desktop?: boolean
  /** The page's phone side gutter in px, which the sheet's side inset cancels. */
  phoneGutter?: number
}

const glass = (theme: Theme, solid: boolean) => {
  const dark = theme.palette.mode === 'dark'
  const ground = theme.palette.background.default
  const tint = solid ? SOLID_TINT : CARD_TINT
  const [t0, t1] = dark ? tint.dark : tint.light
  return {
    content: '""',
    position: 'absolute',
    zIndex: -1,
    pointerEvents: 'none',
    // Follows `--auth-scale` (authScale.ts) so the card keeps its proportions
    // round a scaled column; unset, this is -44/-40px and 16px.
    inset: `${authPx(-44)} ${authPx(-40)} ${authPx(-40)} ${authPx(-40)}`,
    borderRadius: authPx(16),
    background: `linear-gradient(160deg, ${
      dark ? `rgba(255,255,255,${solid ? 0.06 : 0.05})` : `rgba(255,255,255,${solid ? 0.5 : 0.35})`
    }, rgba(255,255,255,0) 45%), linear-gradient(160deg, ${alpha(ground, t0)}, ${alpha(ground, t1)})`,
    backdropFilter: 'blur(56px) saturate(150%)',
    WebkitBackdropFilter: 'blur(56px) saturate(150%)',
    border: `1px solid ${dark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.7)'}`,
    boxShadow: `inset 0 1px 0 ${dark ? `rgba(255,255,255,${solid ? 0.16 : 0.14})` : 'rgba(255,255,255,0.9)'}, 0 24px 60px -24px ${
      dark ? 'rgba(0,0,0,0.6)' : 'rgba(40,20,90,0.16)'
    }`,
  } as const
}

export const authGlassCardSx = (theme: Theme, opts: CardOpts = {}) => {
  const gutter = opts.phoneGutter ?? 12
  return {
    position: 'relative',
    isolation: 'isolate',
    // PHONES — every auth page, solid. `down('sm')` rather than an unconditional
    // base rule, so phone and desktop never compete for the same pseudo-element
    // (an unscoped base + `up('sm')` override did not reliably let the wider
    // desktop inset win). The side inset is the page's own phone gutter, so the
    // panel lands flush with the screen: a full-width sheet with rounded top and
    // bottom and no side borders, rather than a card squeezed to fit.
    [theme.breakpoints.down('sm')]: {
      '&::before': {
        ...glass(theme, true),
        inset: `-24px -${gutter}px -24px -${gutter}px`,
        borderRadius: '20px',
        borderLeft: 0,
        borderRight: 0,
      },
    },
    ...(opts.desktop === false
      ? {}
      : {
          [theme.breakpoints.up('sm')]: {
            '&::before': glass(theme, opts.density === 'solid'),
          },
        }),
  } as const
}
